// components/calendar/CalendarDialogs.js
// Modal per nuovo appuntamento, modifica e cancellazione

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, CalendarIcon, Clock, User, Building, Users, Mail } from 'lucide-react';
import { format } from 'date-fns';

// New Appointment Modal
export const NewAppointmentModal = ({
  open,
  onOpenChange,
  newAppointment,
  setNewAppointment,
  leads,
  technicians,
  rooms,
  teamGroups,
  durationOptions,
  isAdmin,
  isProjectManager,
  isTechnician,
  currentUserId,
  onSubmit,
  saving
}) => {
  const showLeadField = ['sopralluogo', 'sede', 'scelta_extra'].includes(newAppointment.appointment_type);
  const showTechnicianField = ['sopralluogo', 'sede', 'scelta_extra'].includes(newAppointment.appointment_type);
  const showRoomField = ['sede', 'interno'].includes(newAppointment.appointment_type);
  const showTeamField = newAppointment.appointment_type === 'interno';
  const showTitleField = ['interno', 'scadenza_contabile', 'personale'].includes(newAppointment.appointment_type);
  const showEmailToggle = ['sopralluogo', 'sede', 'scelta_extra'].includes(newAppointment.appointment_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#FF7819]" />
            Nuovo Appuntamento
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Appointment Type */}
          <div>
            <Label>Tipo Appuntamento</Label>
            <Select 
              value={newAppointment.appointment_type} 
              onValueChange={(v) => setNewAppointment(prev => ({ ...prev, appointment_type: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="appointment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sopralluogo">Sopralluogo (cliente)</SelectItem>
                <SelectItem value="sede">Appuntamento in Sede</SelectItem>
                <SelectItem value="interno">Riunione Interna</SelectItem>
                <SelectItem value="scelta_extra">Scelta Extra</SelectItem>
                {(isAdmin || isProjectManager) && (
                  <SelectItem value="scadenza_contabile">Scadenza Contabile</SelectItem>
                )}
                <SelectItem value="personale">Personale</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Title field for internal/accounting/personal */}
          {showTitleField && (
            <div>
              <Label>Titolo</Label>
              <Input
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Es: Riunione settimanale"
                className="mt-1"
                data-testid="appointment-title-input"
              />
            </div>
          )}

          {/* Lead selection */}
          {showLeadField && (
            <div>
              <Label>Cliente (Lead)</Label>
              <Select 
                value={newAppointment.lead_id} 
                onValueChange={(v) => setNewAppointment(prev => ({ ...prev, lead_id: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="lead-select">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map(lead => (
                    <SelectItem key={lead.id} value={lead.id}>
                      {lead.first_name} {lead.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Technician selection */}
          {showTechnicianField && (isAdmin || isProjectManager) && (
            <div>
              <Label>Tecnico Assegnato</Label>
              <Select 
                value={newAppointment.assigned_technician_id} 
                onValueChange={(v) => setNewAppointment(prev => ({ ...prev, assigned_technician_id: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="technician-select">
                  <SelectValue placeholder="Seleziona tecnico" />
                </SelectTrigger>
                <SelectContent>
                  {technicians.map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>
                      {tech.first_name} {tech.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Room selection */}
          {showRoomField && (
            <div>
              <Label>Sala Riunioni</Label>
              <Select 
                value={newAppointment.room_id} 
                onValueChange={(v) => setNewAppointment(prev => ({ ...prev, room_id: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="room-select">
                  <SelectValue placeholder="Seleziona sala (opzionale)" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.filter(r => r.is_active).map(room => (
                    <SelectItem key={room.id} value={room.id}>
                      <div className="flex items-center gap-2">
                        <Building className="w-3 h-3" />
                        {room.name} ({room.capacity} posti)
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Team groups for internal meetings */}
          {showTeamField && (
            <div>
              <Label>Gruppi Partecipanti</Label>
              <div className="mt-2 space-y-2">
                {teamGroups.map(group => (
                  <label key={group.value} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <Checkbox
                      checked={newAppointment.team_groups.includes(group.value)}
                      onCheckedChange={(checked) => {
                        setNewAppointment(prev => ({
                          ...prev,
                          team_groups: checked 
                            ? [...prev.team_groups, group.value]
                            : prev.team_groups.filter(g => g !== group.value)
                        }));
                      }}
                    />
                    <span className="text-sm">{group.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={format(newAppointment.appointment_date, 'yyyy-MM-dd')}
                onChange={(e) => {
                  const newDate = new Date(e.target.value);
                  const oldDate = newAppointment.appointment_date;
                  newDate.setHours(oldDate.getHours(), oldDate.getMinutes());
                  setNewAppointment(prev => ({ ...prev, appointment_date: newDate }));
                }}
                className="mt-1"
                data-testid="appointment-date-input"
              />
            </div>
            <div>
              <Label>Ora</Label>
              <Input
                type="time"
                value={format(newAppointment.appointment_date, 'HH:mm')}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newDate = new Date(newAppointment.appointment_date);
                  newDate.setHours(parseInt(hours), parseInt(minutes));
                  setNewAppointment(prev => ({ ...prev, appointment_date: newDate }));
                }}
                className="mt-1"
                data-testid="appointment-time-input"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label>Durata</Label>
            <Select 
              value={String(newAppointment.duration_minutes)} 
              onValueChange={(v) => setNewAppointment(prev => ({ ...prev, duration_minutes: parseInt(v) }))}
            >
              <SelectTrigger className="mt-1" data-testid="duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {durationOptions.map(opt => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <Label>Note</Label>
            <Textarea
              value={newAppointment.notes}
              onChange={(e) => setNewAppointment(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Note aggiuntive..."
              className="mt-1"
              rows={2}
              data-testid="appointment-notes-textarea"
            />
          </div>

          {/* Email notification toggle */}
          {showEmailToggle && (
            <label className="flex items-center gap-2 p-2 bg-blue-50 rounded cursor-pointer">
              <Checkbox
                checked={newAppointment.send_email_to_client}
                onCheckedChange={(checked) => setNewAppointment(prev => ({ ...prev, send_email_to_client: checked }))}
              />
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-sm">Invia email di conferma al cliente</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button 
            className="btn-primary"
            onClick={onSubmit}
            disabled={saving}
            data-testid="create-appointment-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creazione...
              </>
            ) : (
              'Crea Appuntamento'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Appointment Modal
export const EditAppointmentModal = ({
  open,
  onOpenChange,
  appointment,
  editData,
  setEditData,
  onSubmit,
  saving
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Modifica Appuntamento</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Nuova Data</Label>
            <Input
              type="date"
              value={format(editData.appointment_date, 'yyyy-MM-dd')}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                const oldDate = editData.appointment_date;
                newDate.setHours(oldDate.getHours(), oldDate.getMinutes());
                setEditData(prev => ({ ...prev, appointment_date: newDate }));
              }}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Nuova Ora</Label>
            <Input
              type="time"
              value={format(editData.appointment_date, 'HH:mm')}
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(':');
                const newDate = new Date(editData.appointment_date);
                newDate.setHours(parseInt(hours), parseInt(minutes));
                setEditData(prev => ({ ...prev, appointment_date: newDate }));
              }}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label>Motivo modifica (opzionale)</Label>
          <Textarea
            value={editData.reason}
            onChange={(e) => setEditData(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Es: Richiesta del cliente..."
            className="mt-1"
            rows={2}
          />
        </div>

        <label className="flex items-center gap-2 p-2 bg-blue-50 rounded cursor-pointer">
          <Checkbox
            checked={editData.send_email}
            onCheckedChange={(checked) => setEditData(prev => ({ ...prev, send_email: checked }))}
          />
          <Mail className="w-4 h-4 text-blue-600" />
          <span className="text-sm">Invia email di notifica al cliente</span>
        </label>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          className="btn-primary"
          onClick={onSubmit}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            'Salva Modifiche'
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Cancel Appointment Modal
export const CancelAppointmentModal = ({
  open,
  onOpenChange,
  appointment,
  cancelData,
  setCancelData,
  onSubmit,
  saving
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="text-red-600">Annulla Appuntamento</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <p className="text-sm text-[#737373]">
          Sei sicuro di voler annullare questo appuntamento? Questa azione non può essere annullata.
        </p>

        <div>
          <Label>Motivo annullamento</Label>
          <Textarea
            value={cancelData.reason}
            onChange={(e) => setCancelData(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Es: Richiesta del cliente, imprevisti..."
            className="mt-1"
            rows={2}
          />
        </div>

        {appointment?.lead_id && (
          <label className="flex items-center gap-2 p-2 bg-red-50 rounded cursor-pointer">
            <Checkbox
              checked={cancelData.send_email}
              onCheckedChange={(checked) => setCancelData(prev => ({ ...prev, send_email: checked }))}
            />
            <Mail className="w-4 h-4 text-red-600" />
            <span className="text-sm">Invia email di annullamento al cliente</span>
          </label>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Indietro
        </Button>
        <Button 
          variant="destructive"
          onClick={onSubmit}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Annullamento...
            </>
          ) : (
            'Conferma Annullamento'
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
